/**
 * "More filters" — the quick-filter row's overflow.
 *
 * Unread / Starred / Attachments stay as always-visible pills because they are
 * the ones reached constantly. Everything rarer lives behind this one icon, so
 * the toolbar doesn't grow a row of pills nobody uses. The trade is
 * discoverability, which the caller pays back by rendering a chip for every
 * ACTIVE selection back in the pill row (see {@link describeMoreFilters}) — an
 * applied filter is never hidden inside a closed popover.
 *
 * Three dimensions, all server-side (they narrow the list, the search and the
 * By Sender / By Category overviews identically):
 *   • reply state — mail I answered vs never answered (inbound inbox only),
 *   • address book — whether the sender is one of my contacts,
 *   • age — a "newer than" and/or an "older than" bound.
 */

import { Filter } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { FilterTri } from '../lib/quick-filters';

/**
 * Age presets, as a span in days. "Older than" becomes a `receivedBefore`
 * bound and "newer than" a `receivedAfter` one, both measured back from now.
 */
export const AGE_PRESETS = [
  { value: '1d', label: '1 day', days: 1 },
  { value: '3d', label: '3 days', days: 3 },
  { value: '1w', label: '1 week', days: 7 },
  { value: '2w', label: '2 weeks', days: 14 },
  { value: '1m', label: '1 month', days: 30 },
  { value: '3m', label: '3 months', days: 90 },
  { value: '6m', label: '6 months', days: 180 },
  { value: '1y', label: '1 year', days: 365 },
] as const;

export type AgePreset = (typeof AGE_PRESETS)[number]['value'] | '';

export interface MoreFiltersState {
  /** 'is' = I replied, 'not' = never replied. */
  replied: FilterTri;
  /** 'is' = sender is a contact, 'not' = sender is a stranger. */
  addressBook: FilterTri;
  /** Keep only mail older than this. */
  olderThan: AgePreset;
  /** Keep only mail newer than this. */
  newerThan: AgePreset;
}

export const EMPTY_MORE_FILTERS: MoreFiltersState = {
  replied: 'off',
  addressBook: 'off',
  olderThan: '',
  newerThan: '',
};

export function moreFiltersActive(s: MoreFiltersState): boolean {
  return s.replied !== 'off' || s.addressBook !== 'off' || !!s.olderThan || !!s.newerThan;
}

const ageLabel = (v: AgePreset) => AGE_PRESETS.find((p) => p.value === v)?.label ?? '';

/**
 * The active selections as `{ key, label, clear }` chips for the pill row —
 * this is what keeps an applied filter visible once the popover is closed.
 */
export function describeMoreFilters(
  state: MoreFiltersState,
  onChange: (next: MoreFiltersState) => void,
): { key: keyof MoreFiltersState; label: string; clear: () => void }[] {
  const out: { key: keyof MoreFiltersState; label: string; clear: () => void }[] = [];
  if (state.replied !== 'off') {
    out.push({
      key: 'replied',
      label: state.replied === 'is' ? 'I replied' : 'Never replied',
      clear: () => onChange({ ...state, replied: 'off' }),
    });
  }
  if (state.addressBook !== 'off') {
    out.push({
      key: 'addressBook',
      label: state.addressBook === 'is' ? 'In address book' : 'Not in address book',
      clear: () => onChange({ ...state, addressBook: 'off' }),
    });
  }
  if (state.olderThan) {
    out.push({
      key: 'olderThan',
      label: `Older than ${ageLabel(state.olderThan)}`,
      clear: () => onChange({ ...state, olderThan: '' }),
    });
  }
  if (state.newerThan) {
    out.push({
      key: 'newerThan',
      label: `Newer than ${ageLabel(state.newerThan)}`,
      clear: () => onChange({ ...state, newerThan: '' }),
    });
  }
  return out;
}

/**
 * Resolves the state into the wire filters the list/search/group reads take.
 *
 * The age bounds are computed from a clock FLOORED TO THE HOUR on purpose: they
 * land in the React Query key, and a raw `Date.now()` would mint a new key on
 * every render and refetch forever. An hour of slack is immaterial to a filter
 * whose finest preset is a day.
 */
export function moreFiltersToQuery(s: MoreFiltersState): {
  answered?: boolean;
  inAddressBook?: boolean;
  receivedBefore?: string;
  receivedAfter?: string;
} {
  const HOUR = 3_600_000;
  const now = Math.floor(Date.now() / HOUR) * HOUR;
  const ago = (v: AgePreset) => {
    const preset = AGE_PRESETS.find((p) => p.value === v);
    return preset ? new Date(now - preset.days * 24 * HOUR).toISOString() : undefined;
  };
  return {
    answered: s.replied === 'off' ? undefined : s.replied === 'is',
    inAddressBook: s.addressBook === 'off' ? undefined : s.addressBook === 'is',
    receivedBefore: ago(s.olderThan),
    receivedAfter: ago(s.newerThan),
  };
}

function TriRow({
  label,
  value,
  isLabel,
  notLabel,
  onChange,
}: {
  label: string;
  value: FilterTri;
  isLabel: string;
  notLabel: string;
  onChange: (v: FilterTri) => void;
}) {
  const options: { v: FilterTri; l: string }[] = [
    { v: 'off', l: 'Any' },
    { v: 'is', l: isLabel },
    { v: 'not', l: notLabel },
  ];
  return (
    <div className="space-y-1">
      <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            aria-pressed={value === o.v}
            onClick={() => onChange(o.v)}
            className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
              value === o.v
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border text-muted-foreground hover:bg-accent'
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function AgeRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: AgePreset;
  onChange: (v: AgePreset) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <select
        className="h-7 rounded-md border bg-background px-1 text-xs"
        value={value}
        onChange={(e) => onChange(e.target.value as AgePreset)}
      >
        <option value="">Any</option>
        {AGE_PRESETS.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function MoreFiltersPopover({
  state,
  onChange,
}: {
  state: MoreFiltersState;
  onChange: (next: MoreFiltersState) => void;
}) {
  const active = moreFiltersActive(state);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="More filters"
          aria-pressed={active}
          title="More filters — reply status, address book, age"
          className={`relative inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
            active
              ? 'border-primary bg-primary/10 text-foreground'
              : 'border-border text-muted-foreground hover:bg-accent'
          }`}
        >
          <Filter className="h-3 w-3" /> More
          {active && (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-primary"
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 space-y-3 p-3">
        <TriRow
          label="Replied"
          value={state.replied}
          isLabel="I replied"
          notLabel="Never replied"
          onChange={(replied) => onChange({ ...state, replied })}
        />
        <TriRow
          label="Address book"
          value={state.addressBook}
          isLabel="In my contacts"
          notLabel="Not in contacts"
          onChange={(addressBook) => onChange({ ...state, addressBook })}
        />
        <div className="space-y-1">
          <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Age
          </p>
          <AgeRow
            label="Older than"
            value={state.olderThan}
            onChange={(olderThan) => onChange({ ...state, olderThan })}
          />
          <AgeRow
            label="Newer than"
            value={state.newerThan}
            onChange={(newerThan) => onChange({ ...state, newerThan })}
          />
        </div>
        <div className="flex justify-end border-t pt-2">
          <button
            type="button"
            disabled={!active}
            className="text-[11px] text-muted-foreground hover:underline disabled:opacity-40"
            onClick={() => onChange(EMPTY_MORE_FILTERS)}
          >
            Clear all
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
